import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Team from "@mapmycustomers/shared/types/Team";
import SelectableTeam from "@mapmycustomers/shared/types/ui/SelectableTeam";
import User from "@mapmycustomers/shared/types/User";

import { getCurrentUser } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { RootState } from "@app/store/rootReducer";

import Footer, { type RenderApplyButton } from "./components/Footer";
import TeamList from "./components/TeamList";

export type { RenderApplyButton };
import UserList, { UserListProps } from "./components/UserList";
import styles from "./UsersSelect.module.scss";

interface UsersSelectProps {
  allowEmpty?: boolean;
  allUsersWhenEmpty?: boolean;
  cancelButton?: ReactNode;
  confirmText?: string;
  extraButton?: ReactNode;
  onApply?: (selectedUserIds: User["id"][]) => void;
  onCancel?: () => void;
  preselectedUserIds?: User["id"][];
  renderApplyButton?: RenderApplyButton;
  selectedUserIds?: User["id"][];
  teams: Team[];
  users: User[];
}

const UsersSelect = ({
  allowEmpty,
  allUsersWhenEmpty = true,
  cancelButton,
  confirmText,
  currentUser,
  extraButton,
  onApply,
  onCancel,
  preselectedUserIds,
  renderApplyButton,
  selectedUserIds = [],
  teams,
  users,
}: UsersSelectProps & ReturnType<typeof mapStateToProps>) => {
  const allUserIds = useMemo(() => users.map(({ id }) => id), [users]);
  const ownerUserIds = useMemo(() => users.filter(isOwner).map(({ id }) => id), [users]);
  const noTeamUserIds = useMemo(
    () => users.filter((user) => !isOwner(user) && !user.teams.length).map(({ id }) => id),
    [users]
  );

  const [currentSelectedUserIds, setSelectedUserIds] = useState<User["id"][]>(
    selectedUserIds?.length ? selectedUserIds : allUsersWhenEmpty ? allUserIds : []
  );
  const selectedUserIdsSet = useMemo(
    () => new Set(currentSelectedUserIds),
    [currentSelectedUserIds]
  );

  const isTeamSelected = useCallback(
    (team: SelectableTeam) => {
      switch (team) {
        case "noTeam":
          return noTeamUserIds.every((id) => selectedUserIdsSet.has(id));
        case "owners":
          return ownerUserIds.every((id) => selectedUserIdsSet.has(id));
        default:
          return team.users.every(
            ({ id }) =>
              selectedUserIdsSet.has(id) || (preselectedUserIds && preselectedUserIds.includes(id))
          );
      }
    },
    [noTeamUserIds, ownerUserIds, preselectedUserIds, selectedUserIdsSet]
  );

  const teamsByUserId = useMemo(() => new Map(users.map((user) => [user.id, user.teams])), [users]);

  const handleSelectAll = useCallback(() => setSelectedUserIds(allUserIds), [allUserIds]);
  const handleDeselectAll = useCallback(() => setSelectedUserIds([]), []);

  const handleTeamClick = useCallback(
    (team: SelectableTeam) => {
      const wasSelected = isTeamSelected(team);
      const userIds =
        team === "owners"
          ? ownerUserIds
          : team === "noTeam"
            ? noTeamUserIds
            : team.users
                .filter(({ id, deactivated }) => !deactivated && allUserIds.includes(id))
                .map(({ id }) => id);
      if (!wasSelected) {
        // when adding users, no special verification is needed, just add avoiding duplicates
        setSelectedUserIds((currentSelectedUserIds) =>
          Array.from(new Set([...currentSelectedUserIds, ...userIds]))
        );
      } else {
        const removableUserIds = new Set(
          // when removing owners or no-team users, no special verification needed, since these users
          // are not a part of any other teams except their own
          team === "owners" || team === "noTeam"
            ? userIds
            : userIds.filter((userId) => teamsByUserId.get(userId)!.length < 2)
        );
        setSelectedUserIds((currentSelectedUserIds) =>
          currentSelectedUserIds.filter((id) => !removableUserIds.has(id))
        );
      }
    },
    [isTeamSelected, ownerUserIds, noTeamUserIds, allUserIds, teamsByUserId]
  );

  const handleUserClick = useCallback<NonNullable<UserListProps["onUserClick"]>>((userId) => {
    setSelectedUserIds((currentSelectedUserIds) => {
      const index = currentSelectedUserIds.indexOf(userId);
      if (index >= 0) {
        return [
          ...currentSelectedUserIds.slice(0, index),
          ...currentSelectedUserIds.slice(index + 1),
        ];
      } else {
        return [...currentSelectedUserIds, userId];
      }
    });
  }, []);

  const handleApply = useCallback(() => {
    onApply?.(currentSelectedUserIds);
  }, [onApply, currentSelectedUserIds]);

  return (
    <div className={styles.container}>
      <div className={styles.teamsAndUsersColumns}>
        <TeamList
          className={styles.teams}
          isCurrentUserOwner={isOwner(currentUser)}
          isTeamSelected={isTeamSelected}
          noTeamUserIds={noTeamUserIds}
          onTeamClick={handleTeamClick}
          teams={teams}
        />
        <UserList
          className={styles.users}
          onUserClick={handleUserClick}
          selectedUserIdsSet={selectedUserIdsSet}
          users={users}
        />
      </div>
      <Footer
        allowEmpty={allowEmpty}
        cancelButton={cancelButton}
        className={styles.footer}
        confirmText={confirmText}
        extraButton={extraButton}
        onApply={handleApply}
        onCancel={onCancel}
        onDeselectAll={handleDeselectAll}
        onSelectAll={handleSelectAll}
        renderApplyButton={renderApplyButton}
        selectedUserIds={currentSelectedUserIds}
        users={users}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: getCurrentUser(state)!,
});

export default connect(mapStateToProps)(UsersSelect);
